import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { Flex, Text } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { FixedWidthPopupComponent } from '@atlassian/jira-jql-builder-basic-picker/src/ui/popup-container/index.tsx';
import View from '@atlassian/jira-jql-builder-basic-textarea/src/ui/index.tsx';
import {
	TEXT_CF_TYPE,
	TEXT_READONLY_CF_TYPE,
	URL_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { TextAreaPropTypes } from '../../../../../common/types.tsx';
import PickerTrigger from '../../../../../common/ui/picker-trigger/main.tsx';
import type { TextAreaCommonProps } from '../../../../../common/ui/types.tsx';
import {
	useShouldRenderToParent,
	useVerticalMode,
} from '../../../../../controllers/feature-context.tsx';
import { PickerTriggerLayout } from '../../../../../common/ui/picker-trigger-layout/index.tsx';
import { CHAR_LIMIT_WHEN_ENABLED } from './constants.tsx';
import messages from './messages.tsx';
import { isUnsubmittedValueTooLong, isValidUrl } from './utils.tsx';

export const formatLabel = (label: string, value: string | null, verticalMode = false) => {
	if (value === null || value === undefined || !value.length) {
		return verticalMode ? <PickerTriggerLayout label={label || ''}> </PickerTriggerLayout> : label;
	}

	if (verticalMode) {
		return (
			<PickerTriggerLayout label={label || ''}>
				<ValueWrapper>{value}</ValueWrapper>
			</PickerTriggerLayout>
		);
	}

	return (
		<Flex alignItems="center">
			<Text color="inherit" as="strong">
				{label}
			</Text>
			{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
			{/* eslint-disable react/no-unescaped-entities -- go/jfe-eslint */}: "
			<ValueWrapper>{value}</ValueWrapper>"
			{/* eslint-enable react/no-unescaped-entities -- go/jfe-eslint */}
		</Flex>
	);
};

export const TextAreaPickerPopup = ({
	fieldId,
	fieldDisplayName,
	propType,
	fieldType,
	isOpen,
	onOpen,
	onClose,
	onClear,
	onChange,
	onSendOptionAnalytics,
	value,
	fieldDescription,
	isIrremovable,
}: TextAreaCommonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const shouldRenderToParent = useShouldRenderToParent();
	const verticalMode = useVerticalMode();
	/**
	 * Note - despite the name, this actually handles the onClick
	 * of the 'Update' button that appears in the footer of the picker content,
	 * it does not get called on a change event
	 */
	const handleOnChange = useCallback(
		(inputValue: string) => {
			onChange(inputValue);
			onClose();
			onSendOptionAnalytics &&
				onSendOptionAnalytics({
					searchStringLength: inputValue.length,
				});
		},
		[onChange, onClose, onSendOptionAnalytics],
	);

	const handleOnTriggerClick = useCallback(() => {
		isOpen ? onClose() : onOpen();
	}, [isOpen, onClose, onOpen]);

	const getTextAreaPickerErrorMessage = useCallback(
		(inputValue: string): string | undefined => {
			if (
				(fieldType === TEXT_READONLY_CF_TYPE ||
					fieldType === TEXT_CF_TYPE ||
					propType === TextAreaPropTypes.ExactText) &&
				isUnsubmittedValueTooLong(inputValue)
			) {
				return formatMessage(messages.characterLimitErrorMessage, {
					maxChars: CHAR_LIMIT_WHEN_ENABLED,
				});
			}

			if (fieldType === URL_CF_TYPE && !isValidUrl(inputValue)) {
				return formatMessage(messages.invalidUrlErrorMessage);
			}

			return undefined;
		},
		[fieldType, formatMessage, propType],
	);

	return (
		<Popup
			key={fieldId}
			placement="bottom-start"
			isOpen={isOpen}
			onClose={onClose}
			popupComponent={FixedWidthPopupComponent}
			shouldRenderToParent={shouldRenderToParent}
			messageId="jql-builder-basic.ui.jql-editor.field.text-area-picker.text-area-picker-popup.popup"
			messageType="transactional"
			trigger={({ ref }) => (
				<PickerTrigger
					isInvalid={false}
					isSelected={isOpen}
					hasValues={!!value}
					onClick={handleOnTriggerClick}
					onClear={onClear}
					ref={ref}
					testId={`jql-builder-basic.ui.jql-editor.text-area.filter-button.${fieldId}`}
					fieldDescription={fieldDescription}
					isIrremovable={isIrremovable}
				>
					{formatLabel(fieldDisplayName, value, verticalMode)}
				</PickerTrigger>
			)}
			content={({ setInitialFocusRef }) => (
				<View
					setInitialFocusRef={setInitialFocusRef}
					value={value || ''}
					getErrorMessage={getTextAreaPickerErrorMessage}
					onChange={handleOnChange}
					multiline={fieldType !== URL_CF_TYPE}
				/>
			)}
		/>
	);
};

export default TextAreaPickerPopup;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ValueWrapper = styled.span({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '200px',
});
